import {
  selectIsAuthenticationLoading,
  selectUserInfos,
} from "domain/auth/auth.selectors";
import { useForm } from "react-hook-form";
import { Trans } from "react-i18next";
import { useAppDispatch, useAppSelector } from "ui/hook/store";
import { translate } from "ui/i18n";
import { INInput } from "ui/pages/app/components/inputs/INInput";
import { Inputs } from "ui/pages/inputsType";
import { PrimaryButton } from "ui/pages/app/components/buttons/PrimaryButton";
import { updatePassword } from "domain/auth/auth.actions";
import { Snackbar } from "ui/pages/snackbar";
import { BackButton } from "ui/pages/app/components/buttons/BackButton";
import { useWindowWidth } from "ui/utils/hooks";
import { useNavigate } from "react-router-dom";
import { i18nKey } from "domain/entities/activities.entities";

export function ChangePassword() {
  const dispatch = useAppDispatch();
  const isLoading = useAppSelector(selectIsAuthenticationLoading);
  const { email } = useAppSelector(selectUserInfos);
  const isDesktop = useWindowWidth();
  const navigate = useNavigate();
  const { register, handleSubmit, formState, watch, setError, reset } =
    useForm<Inputs>({
      mode: "onSubmit",
      defaultValues: {
        oldPassword: "",
        password: "",
        passwordConfirmation: "",
      },
    });

  const { errors, isDirty } = formState;

  const handleFormSubmit = async (data: Inputs) => {
    const result = await dispatch(
      updatePassword({
        email,
        password: data.password,
        oldPassword: data.oldPassword,
      })
    ).unwrap();
    if (result.success) {
      Snackbar.showSuccess({
        title: translate("ns1:RecoverPassword.PasswordChanged"),
      });
      reset();
      navigate("/account");
    } else {
      setError(
        "oldPassword",
        { message: translate("ns1:Input.Errors.WrongPassword") },
        {
          shouldFocus: true,
        }
      );
    }
  };

  const validatePassword = (v: string) => {
    return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d\s:]).{8,}$/.test(v);
  };

  return (
    <div className="flex min-h-dvh flex-col bg-darkGrey-100 p-4 md:p-8">
      <BackButton onClick={() => navigate("/account")} hideText={!isDesktop} />
      <div className="mt-5 flex w-full max-w-[700px] flex-col items-center justify-center self-center rounded-[20px] bg-white px-6 py-10 md:p-20">
        <h2 className="text-center">
          {translate("ns1:RecoverPassword.ChangeYourPassword")}
        </h2>

        <div className="w-full">
          <form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="mt-6 md:mt-12"
          >
            <INInput
              type="password"
              error={errors.oldPassword}
              label={translate("ns1:Input.Labels.OldPassword")}
              placeholder={translate("ns1:Input.Labels.OldPassword")}
              register={{
                ...register("oldPassword", {
                  required: translate("ns1:Input.Errors.Required"),
                }),
              }}
            />

            <INInput
              className="mt-2"
              type="password"
              error={errors.password}
              label={translate("ns1:Input.Labels.NewPassword")}
              placeholder={translate("ns1:Input.Labels.NewPassword")}
              register={{
                ...register("password", {
                  required: translate("ns1:Input.Errors.Required"),
                  validate: {
                    passwordFormat: (v) => validatePassword(v),
                  },
                }),
              }}
            />
            <div
              className={`mt-2 text-xs leading-5 text-darkGrey-600 md:mt-4 ${
                errors.password ? "text-error" : ""
              }`}
            >
              <Trans
                i18nKey={"Input.Errors.PasswordFormat" as i18nKey}
                components={[<span className="font-extrabold" />]}
              />
            </div>

            <INInput
              className="mt-4 md:mt-8"
              type="password"
              error={errors.passwordConfirmation}
              label={translate("ns1:Input.Labels.ConfirmPassword")}
              placeholder={translate("ns1:Input.Labels.ConfirmPassword")}
              register={{
                ...register("passwordConfirmation", {
                  required: translate("ns1:Input.Errors.Required"),
                  validate: (v) =>
                    watch("password") === v ||
                    translate("ns1:RecoverPassword.PasswordDontMatch"),
                }),
              }}
            />

            <PrimaryButton
              className="mt-6 md:mt-20"
              type="submit"
              disabled={!isDirty || isLoading}
              width="100%"
              height="52px"
              onClick={handleSubmit(handleFormSubmit)}
            >
              {translate("ns1:Button.Validate")}
            </PrimaryButton>
          </form>
        </div>
      </div>
    </div>
  );
}
